import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { IWeissmanModalComponent } from '../../../Compliance/WeissmanModalService';
import { PaymentBatchService } from '../../paymentBatch.service';
import { BusyIndicatorService } from '../../../Busy-Indicator';
import { ToastrService } from 'ngx-toastr';
import {
    AgGridMultiSelectTracker,
    AgGridMultiSelectCellRendererParams,
    AgGridMultiSelectRendererParams,
    AgGridMultiSelectedCellRenderer,
    AgGridMultiSelectedHeaderRenderer
 } from '../../../Compliance/AgGrid/MultiSelectTracker';
import { AgGridOptionsBuilder, AgGridColumns, AgGridFilterParams } from '../../../Compliance/AgGrid';
import { GridApi, GridOptions, GridReadyEvent, ColDef, ColumnState } from 'ag-grid-community';
import { Subject, Subscription } from 'rxjs';
import { DecimalPipe, CurrencyPipe } from '@angular/common';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { HelpService } from '../../../UI-Lib/Help-Tooltip';
import { PAYMENT_BATCH_DETAILS_HELP } from '../paymentBatchDetails.component.help';
import moment from 'moment';
import * as _ from 'lodash';
import { WeissmanKeyValueDisplayPipe } from '../../../UI-Lib/Pipes';
import { WeissmanDateFormat, WeissmanDateFormatPipe } from '../../../UI-Lib/Pipes/Date-Format/date-formatting.pipe';

@Component({
    selector: 'payment-batch-add-payments-modal',
    template: `
        <ws-modal-wrapper
            headerLabel="Add Payments"
            headerHelpContentId="payment-batch-details.add-payments-modal"
            (save)="save()"
            (cancel)="cancel()"
            [disableSave]="!canSave"
        >
            <div class="d-flex align-items-center justify-content-between mt-4">
                <ag-grid-row-count
                    [gridApi]="gridApi"
                    [gridTracker]="gridTracker"
                    [gridOptions]="gridOptions"
                ></ag-grid-row-count>
                <div class="d-flex align-items-center gap-2">
                    <label class="text-nowrap me-2 mb-0">Due Date Range: </label>
                    <div class="input-group">
                        <weissman-datetime
                            [(inputDate)]="fromDate"
                            [dateOnly]="true"
                            [timeZone]="'Stored'"
                            #startDatePicker
                        >
                        </weissman-datetime>
                    </div>
                    <span class="mx-2">to</span>
                    <div class="input-group">
                        <weissman-datetime
                            [(inputDate)]="toDate"
                            [dateOnly]="true"
                            [timeZone]="'Stored'"
                            [rangeEnd]="true"
                            #endDatePicker
                        >
                        </weissman-datetime>
                    </div>
                    <button class="ace-btn btn-sm btn-primary" (click)="refresh()"> Refresh </button>
                </div>
            </div>
            <ws-ag-grid-angular
                [gridOptions]="gridOptions"
                (gridReady)="onAgGridReady($event)"
                [gridId]="gridId"
                [overlayNoRowsTemplate]="overlayNoRowsString"
            >
            </ws-ag-grid-angular>
        </ws-modal-wrapper>
    `,
    standalone: false,
})
export class PaymentBatchAddPaymentsModalComponent
    implements OnInit, OnDestroy, IWeissmanModalComponent<number, Core.PaymentBatchAddPaymentsResultModel>
{
    constructor(
        private readonly _paymentBatchService: PaymentBatchService,
        private readonly _busyIndicatorService: BusyIndicatorService,
        private readonly _toastr: ToastrService,
        private readonly _helpService: HelpService,
        public readonly decimalPipe: DecimalPipe,
        public readonly currencyPipe: CurrencyPipe,
        private readonly _datePipe: WeissmanDateFormatPipe,
        private readonly _keyValueDisplayPipe: WeissmanKeyValueDisplayPipe,
        private readonly _bsModalRef: BsModalRef,
    ) {}

    params: number;
    result: Core.PaymentBatchAddPaymentsResultModel;

    paymentBatchId: number;
    fromDate: Date | undefined;
    toDate: Date | undefined;
    canGetRows: boolean = false;
    loadingGrid: boolean = false;

    gridResults: Core.PaymentBatchAddPaymentsGridFetchResults;

    gridId: System.Guid = '371FAAE8-8FEA-48D2-9F5D-7875F5ADEAA0';
    gridTracker: AgGridMultiSelectTracker;
    gridOptions: GridOptions = new AgGridOptionsBuilder({
        suppressScrollOnNewData: true,
        onFilterChanged: () => {
            (this.gridTracker.onGridFilterChanged(), this._updateTotals());
        },
        onSortChanged: () => this.gridTracker.onGridSortChanged(),
        onGridColumnsChanged: () => {
            this.gridApi.sizeColumnsToFit();
        },
        onSelectionChanged: () => {
            this._updateTotals();
        },
        rowClassRules: {
            'ag-row-selected': (params) => params.data && this.gridTracker.isRowSelected(params.data.paymentId),
            'selected-totals-row': (params) => params.data && params.data.selectedTotalsRow,
        },
    })
        .withContext(this)
        .withLoadingOverlay()
        .withColumnResize()
        .withMultipleColumnSort()
        .withTextSelection()
        .withColumnPinning()
        .build();
    gridApi: GridApi;
    selectedRowsModel: Compliance.SelectedRowsModel;

    private _gridMultiSelectSub: Subscription;
    private _destroy$: Subject<void> = new Subject();

    ngOnInit() {
        this._helpService.setContent(PAYMENT_BATCH_DETAILS_HELP);
        this.paymentBatchId = this.params;
        const initfromDate = moment(new Date()).subtract(2, 'months').startOf('month').toDate();
        const initToDate = moment(new Date()).add(3, 'months').endOf('month').toDate();
        this.fromDate = this.normalizeDate(initfromDate, false);
        this.toDate = this.normalizeDate(initToDate, true);
        //// Leaving these console logs here in case we need to debug date normalization issues again
        // console.log('Initial fromDate:', initfromDate, ' TZ offset:', initfromDate.getTimezoneOffset());
        // console.log('Initial toDate:', initToDate, ' TZ offset:', initToDate.getTimezoneOffset());
        // console.log('Normalized Init fromDate:', this.fromDate, ' TZ offset:', this.fromDate.getTimezoneOffset());
        // console.log('Normalized Init toDate:', this.toDate, ' TZ offset:', this.toDate.getTimezoneOffset());

        this.selectedRowsModel = {
            selectAllRows: false,
            selectedRows: [],
        };

        this.result = {
            paymentBatchId: this.paymentBatchId,
            succeeded: false,
            addedCount: 0,
        };
    }

    ngOnDestroy(): void {
        this._gridMultiSelectSub && this._gridMultiSelectSub.unsubscribe();
        this._destroy$.next();
        this._destroy$.complete();
    }

    async onAgGridReady(event: GridReadyEvent): Promise<void> {
        this.gridApi = event.api;
        this.gridApi.setGridOption('loading', false);
        this.gridTracker = new AgGridMultiSelectTracker(this.gridOptions, this.gridApi, this._getGridRowIds.bind(this));

        this._gridMultiSelectSub = this.gridTracker.selectedRows$.subscribe(async (x) => {
            this.selectedRowsModel = x;
            this._updateTotals();
        });
    }

    get overlayNoRowsString(): string {
        return this.canGetRows ? undefined : 'Select a date range and click Refresh to see available payments';
    }

    get canSave(): boolean {
        if (!this.canGetRows || !this.selectedRowsModel) return false;
        return this.selectedRowsModel.selectedRows.length > 0 || this.selectedRowsModel.selectAllRows;
    }

    async save() {
        const busyRef = this._busyIndicatorService.show({ message: 'Adding payments...' });

        try {
            const selectedRows = this._getSelectedRows();
            const request: Core.PaymentBatchAddPaymentsRequestModel = {
                paymentBatchId: this.paymentBatchId,
                selectedPaymentIds: selectedRows.map((x) => x.paymentId),
            };

            this.result = await this._paymentBatchService.addPaymentsToPaymentBatch(request.paymentBatchId, request);
            if (!this.result.succeeded) {
                this._toastr.error(this.result.errorMessage);
                return;
            }

            this._bsModalRef.hide();
        } catch (e) {
            const errorMessage = e && e.error && e.error.message ? e.error.message : 'Unexpected error without message';
            this._toastr.error(errorMessage);
        } finally {
            busyRef.hide();
        }
    }

    cancel(): void {
        this._bsModalRef.hide();
    }

    async refresh(): Promise<void> {
        if (!this.canGetRows) this.canGetRows = true;
        return await this._loadData();
    }

    private async _loadData(): Promise<void> {
        this.loadingGrid = true;
        this.gridApi.setGridOption('loading', true);

        try {
            this.gridTracker.clear(false);

            //// Leaving these console logs here in case we need to debug date normalization issues again
            // console.log('Load fromDate:', this.fromDate, ' TZ offset:', this.fromDate.getTimezoneOffset());
            // console.log('Load toDate:', this.toDate, ' TZ offset:', this.toDate.getTimezoneOffset());
            // let fromDateNormalized = this.normalizeDate(this.fromDate, false);
            // let toDateNormalized = this.normalizeDate(this.toDate, true);
            // console.log(
            //     'Normalized Load fromDate:',
            //     fromDateNormalized,
            //     ' TZ offset:',
            //     fromDateNormalized?.getTimezoneOffset(),
            // );
            // console.log(
            //     'Normalized Load toDate:',
            //     toDateNormalized,
            //     ' TZ offset:',
            //     toDateNormalized?.getTimezoneOffset(),
            // );
            // fromDateNormalized = this.fromDate;
            // toDateNormalized = this.toDate;

            const gridSearchModel: Core.PaymentBatchAddPaymentsGridSearchModel = {
                paymentBatchId: this.paymentBatchId,
                fromDate: this.fromDate,
                toDate: this.toDate,
            };
            this.gridResults = await this._paymentBatchService.getPaymentBatchAddPaymentsList(gridSearchModel);

            this._refreshRowData();
        } finally {
            this.gridApi.setGridOption('loading', false);
            this.loadingGrid = false;
        }
    }

    private normalizeDate(date: Date, rangeEnd: boolean): Date | undefined {
        if (!date) {
            return undefined;
        }

        let normalizedDate = new Date(date);
        const offset = normalizedDate.getTimezoneOffset();
        //// Leaving these console logs here in case we need to debug date normalization issues again
        // console.log('Date:', date, ' Timezone offset:', date.getTimezoneOffset(), ' Range end:', rangeEnd);
        // console.log('Date Normalized:', normalizedDate, 'Timezone offset:', offset);
        if (rangeEnd) {
            // Subtract the timezone offset from the end date to ensure that the date doesn't show as the next day.
            normalizedDate.setMinutes(normalizedDate.getMinutes() - Math.abs(offset));
        } else {
            // Add the timezone offset from the start date to ensure that the date is doesn't show as the previous day.
            normalizedDate.setMinutes(normalizedDate.getMinutes() + Math.abs(offset));
        }
        // console.log('Date Adjusted:', normalizedDate);

        return normalizedDate;
    }

    private _refreshRowData(): void {
        if (!this.gridApi) {
            return;
        }

        // Get current column states
        const colStates: ColumnState[] = this.gridApi.getColumnState();

        const columns = this.getGridColumns();

        this.gridApi.setGridOption('columnDefs', columns);

        // Restore prior column states
        this.gridApi.applyColumnState({ state: colStates, applyOrder: true });

        this.gridApi.setGridOption('rowData', this._getRowData());

        this._updateTotals();
    }

    private _getRowData(): Core.PaymentBatchAddPaymentModel[] {
        return _.chain(this.gridResults.entries).cloneDeep().sortBy(['dueDate', 'collector', 'parcelAcctNum']).value();
    }

    private _updateTotals(): void {
        const selectedRows = this._getSelectedRows();

        if (selectedRows.length === 0) {
            this.gridApi.setGridOption('pinnedBottomRowData', []);
        } else {
            const model = {};
            const selectedEntry: any = {
                model,
                getModel: () => {
                    return model;
                },
                displayPipe: this._keyValueDisplayPipe,
                index: 0,
                paymentAmount: selectedRows.reduce((acc, y) => (acc += y.paymentAmount || 0), 0),
                installmentName: 'SELECTED',
                selectedTotalsRow: true,
            };

            this.gridApi.setGridOption('pinnedBottomRowData', [selectedEntry]);
        }
    }

    private _getGridRowIds(skip: number = 0, take: number = 2147483646): Promise<Compliance.QueryResultModel<number>> {
        const model = { rowsToDisplay: [] };
        this.gridApi.forEachNodeAfterFilterAndSort((x) => model.rowsToDisplay.push(x));
        const rows = model.rowsToDisplay.slice(skip, take + 1);
        const queryResultModel: Compliance.QueryResultModel<number> = {
            lastModifiedTimestamp: new Date(),
            totalRows: rows.length,
            totalValidRows: rows.length,
            data: rows.map((x) => {
                const entry = x.data as Core.PaymentBatchDetailsEntryModel;
                return entry && entry.paymentId;
            }),
        };

        return Promise.resolve(queryResultModel);
    }

    private _getSelectedRows(): Core.PaymentBatchAddPaymentModel[] {
        if (
            !this.gridTracker ||
            !this.gridResults ||
            !this.gridResults.entries ||
            this.gridResults.entries.length == 0
        ) {
            return [];
        }

        // If All is checked and no rows are unchecked, selectedRowsModel will have selectAllRows=true
        // and an empty selectedRows. Return all rows.
        // If All is checked and some rows are unchecked, selectedRowsModel will have selectAllRows=true
        // and selectedRows with the unchecked rows. Return all rows but what is in selectedRows.
        // If All is not checked and some rows are unchecked, selectedRowsModel will have selectAllRows=false
        // and selectedRows with the checked rows. Return all rows that are in selectedRows.
        return this.gridResults.entries.filter(
            (x) =>
                (this.selectedRowsModel.selectAllRows &&
                    this.selectedRowsModel.selectedRows.indexOf(x.paymentId) === -1) ||
                (!this.selectedRowsModel.selectAllRows &&
                    this.selectedRowsModel.selectedRows.indexOf(x.paymentId) !== -1),
        );
    }

    private getGridColumns(): ColDef[] {
        let cols = this._getDefaultColumns();

        if (this.gridResults.characteristics.length) {
            cols = [...cols, ...this._getPaymentBatchCharacteristicsColumns()];
        }

        return cols;
    }

    private _getDefaultColumns(): ColDef[] {
        let cols: ColDef[] = [
            {
                colId: 'grid-column-multiselect',
                headerName: '',
                field: 'paymentId',
                width: AgGridColumns.selectionColumnWidth,
                suppressSizeToFit: true,
                suppressAutoSize: true,
                suppressColumnsToolPanel: true,
                editable: false,
                pinned: 'left',
                lockPinned: true,
                lockVisible: true,
                lockPosition: true,
                suppressMovable: true,
                headerComponent: AgGridMultiSelectedHeaderRenderer,
                headerComponentParams: { tracker: this.gridTracker } as AgGridMultiSelectRendererParams,
                cellRendererSelector: (params) => {
                    if (!params.data?.selectedTotalsRow) {
                        return {
                            component: AgGridMultiSelectedCellRenderer,
                            params: {
                                tracker: this.gridTracker,
                            } as AgGridMultiSelectCellRendererParams,
                        };
                    }
                },
            } as ColDef,
            {
                headerName: 'Payment Status',
                field: 'paymentStatus',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
            },
            {
                headerName: 'Parcel Acct Num',
                field: 'parcelAcctNum',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
            },
            {
                headerName: 'Site',
                field: 'siteName',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
            },
            {
                headerName: 'State',
                field: 'state',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.stateAbbreviationColumnWidth,
            },
            {
                headerName: 'Collector',
                field: 'collector',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
            },
            {
                headerName: 'Due Date',
                field: 'dueDate',
                width: AgGridColumns.dateColumnWidth,
                filter: 'agDateColumnFilter',
                filterParams: AgGridFilterParams.dateFilterParamsWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.dateFloatingFilterParams,
                valueFormatter: (params) => {
                    const model = params.data as Core.PaymentBatchAddPaymentModel;
                    if (!(model && model.dueDate)) {
                        return '';
                    }

                    return WeissmanDateFormat(model.dueDate, true, 'stored');
                },
                cellClass: 'text-align-right',
            },
            {
                headerName: 'Installment',
                field: 'installmentName',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Payment Amount',
                field: 'paymentAmount',
                width: AgGridColumns.numericColumnWidth,
                filter: 'agNumberColumnFilter',
                filterParams: AgGridFilterParams.numberFilterWithBlankOptionsParams,
                floatingFilterComponentParams: AgGridFilterParams.numberFloatingFilterParams,
                valueFormatter: (x) => this.decimalPipe.transform(x.value, '1.2-2'),
                cellClass: 'text-align-right',
            },
            {
                headerName: 'Output Type',
                field: 'transmittalOutput',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
            },
            {
                headerName: 'Payment Type',
                field: 'paymentType',
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
                width: AgGridColumns.textColumnWidth,
            },
            {
                headerName: 'Assessor',
                field: 'assessorName',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Site Pay Resp',
                field: 'sitePayResp',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Site Payment Resp',
                field: 'sitePaymentResp',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Parcel Pay Resp',
                field: 'parcelPayResp',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
            {
                headerName: 'Parcel Payment Resp',
                field: 'parcelPaymentResp',
                width: AgGridColumns.textColumnWidth,
                filter: 'agTextColumnFilter',
                filterParams: AgGridFilterParams.textFilterParams,
                floatingFilterComponentParams: AgGridFilterParams.textFloatingFilterParams,
            },
        ];

        return cols;
    }

    private _getPaymentBatchCharacteristicsColumns(): ColDef[] {
        const duplicates = this.gridResults.characteristics.reduce((acc, x) => {
            acc[x.descriptorName] = _.has(acc, x.descriptorName);
            return acc;
        }, {});
        return this.gridResults.characteristics.map((x, i) => {
            let type = '';
            if (duplicates[x.descriptorName]) {
                if (x.entityTypeId === 5) {
                    type = 'Site';
                } else if (x.entityTypeId === 6) {
                    type = 'Parcel';
                }
            }

            let filter = 'agTextColumnFilter';
            let filterParams = AgGridFilterParams.textFilterWithBlankOptionsParams;
            let floatingFilterComponentParams = AgGridFilterParams.textFloatingFilterParams;
            let columnWidth: number = AgGridColumns.textColumnMedWidth;
            let valueFormatter: any;
            switch (x.descriptorFieldTypeId) {
                case Core.DescriptorFieldTypes.Number:
                case Core.DescriptorFieldTypes.Currency:
                    filter = 'agNumberColumnFilter';
                    filterParams = AgGridFilterParams.numberWithRangeAndEqualToAndBlankFilterParams;
                    floatingFilterComponentParams = AgGridFilterParams.numberFloatingFilterParams;
                    columnWidth = AgGridColumns.numericColumnWidth;
                    if (x.descriptorFieldTypeId == Core.DescriptorFieldTypes.Currency) {
                        valueFormatter = (params) => {
                            if (params.value === null || params.value === undefined || params.value === '') {
                                return '';
                            }
                            const numCurrency: number = parseFloat(params.value);
                            if (isNaN(numCurrency)) {
                                return '';
                            }
                            return this.formatCurrency(numCurrency);
                        };
                    } else {
                        valueFormatter = (params) => this.decimalPipe.transform(params.value, '1.2-2');
                    }
                    break;
                case Core.DescriptorFieldTypes.Date:
                    filter = 'agDateColumnFilter';
                    filterParams = AgGridFilterParams.dateFilterParamsWithBlankOptionsParams;
                    floatingFilterComponentParams = AgGridFilterParams.dateFloatingFilterParams;
                    columnWidth = AgGridColumns.dateColumnWidth;
                    valueFormatter = (y) => {
                        if (!y.value) {
                            return '';
                        }

                        const d = new Date(y.value);
                        return this._datePipe.transform(d, true);
                    };
                    break;
                case Core.DescriptorFieldTypes.YesNo:
                    filterParams = AgGridFilterParams.booleanFilterWithBlankOptionsParams;
                    floatingFilterComponentParams = AgGridFilterParams.booleanFloatingFilterParams;
                    valueFormatter = (y) => {
                        return y.value.toLowerCase() === 'true' ? 'Yes' : y.value.toLowerCase() === 'false' ? 'No' : '';
                    };
                    break;
            }

            return {
                colId: `characteristic.${type ? `${type}.` : ''}${x.descriptorName.replace(/\s+/g, '')}`,
                headerName: `${type ? `${type} ` : ''}${x.descriptorName}`,
                field: `${type}.${x.descriptorName}`,
                toolPanelClass: 'Characteristics',
                hide: true,
                filter,
                filterParams,
                floatingFilterComponentParams,
                width: columnWidth,
                minWidth: AgGridColumns.textColumnSmallWidth,
                valueFormatter: valueFormatter,
                valueGetter: (y) => {
                    const entry = y.data as Core.ForecastGridEntry;
                    return entry && entry.characteristicValues ? entry.characteristicValues[i] : '';
                },
            };
        });
    }

    formatCurrency(value: number): string {
        if (value == null) {
            return '';
        }

        const formattedVal = this.currencyPipe.transform(Math.abs(value), 'USD', 'symbol-narrow');
        return value < 0 ? `(${formattedVal})` : formattedVal;
    }
}
